import { createIntl } from 'react-intl';
import { bcp47ToDropboxLocale } from 'js/i18n/locale_transforms';
/**
 * Converts a Smartling translation object to a format that can be used by react-intl.
 * @param translation A Smartling translation object.
 * @todo Do this at build time instead.
 */
function convertTranslation(translation) {
    const results = {};
    for (const [key, value] of Object.entries(translation)) {
        if (key === 'smartling') {
            continue;
        }
        const element = value.message;
        results[key] = element;
    }
    return results;
}
/**
 * Returns the language file for the given locale containing translated strings for File Viewer. In rSERVER, we
 * have access to a react-intl context and a langpack that contains these same strings. This file is for use in
 * other contexts where we don't have access to the react-intl context.
 *
 * @warning Do not attempt to use this in metaserver. It is only for use when a react-intl context with metaserver's strings
 * is not available; e.g. in other repos.
 *
 * Throws if the locale is not supported.
 * @param locale A Dropbox-formatted locale.
 */
const fetchFileViewerStringsForLocale = async (locale) => {
    switch (locale) {
        case 'da_DK':
            return convertTranslation((await import('js/file_viewer/lang/da_DK.json')).default);
        case 'de':
            return convertTranslation((await import('js/file_viewer/lang/de.json')).default);
        case 'en_GB':
            return convertTranslation((await import('js/file_viewer/lang/en_GB.json')).default);
        case 'es_ES':
            return convertTranslation((await import('js/file_viewer/lang/es_ES.json')).default);
        case 'es':
            return convertTranslation((await import('js/file_viewer/lang/es.json')).default);
        case 'fr_CA':
            return convertTranslation((await import('js/file_viewer/lang/fr_CA.json')).default);
        case 'fr':
            return convertTranslation((await import('js/file_viewer/lang/fr.json')).default);
        case 'id':
            return convertTranslation((await import('js/file_viewer/lang/id.json')).default);
        case 'it':
            return convertTranslation((await import('js/file_viewer/lang/it.json')).default);
        case 'ja':
            return convertTranslation((await import('js/file_viewer/lang/ja.json')).default);
        case 'ko':
            return convertTranslation((await import('js/file_viewer/lang/ko.json')).default);
        case 'ms':
            return convertTranslation((await import('js/file_viewer/lang/ms.json')).default);
        case 'nb_NO':
            return convertTranslation((await import('js/file_viewer/lang/nb_NO.json')).default);
        case 'nl_NL':
            return convertTranslation((await import('js/file_viewer/lang/nl_NL.json')).default);
        case 'pl':
            return convertTranslation((await import('js/file_viewer/lang/pl.json')).default);
        case 'pt_BR':
            return convertTranslation((await import('js/file_viewer/lang/pt_BR.json')).default);
        case 'ru':
            return convertTranslation((await import('js/file_viewer/lang/ru.json')).default);
        case 'sv_SE':
            return convertTranslation((await import('js/file_viewer/lang/sv_SE.json')).default);
        case 'th_TH':
            return convertTranslation((await import('js/file_viewer/lang/th_TH.json')).default);
        case 'uk_UA':
            return convertTranslation((await import('js/file_viewer/lang/uk_UA.json')).default);
        case 'zh_CN':
            return convertTranslation((await import('js/file_viewer/lang/zh_CN.json')).default);
        case 'zh_TW':
            return convertTranslation((await import('js/file_viewer/lang/zh_TW.json')).default);
        case 'en':
            // In the English case, we don't need to load a file, so we just return an empty object. IntlProvider should
            // use English strings by default.
            return Promise.resolve({});
        default:
            throw new Error(`Unsupported locale provided to File Viewer: ${locale}`);
    }
};
/**
 * Returns an IntlShape suitable for passing into <FileViewer>.
 *
 * @warning Do not attempt to use this in metaserver. It is only for use when a react-intl context with metaserver's strings
 * is not available; e.g. in other repos.
 *
 * @throws if the locale is not supported.
 *
 * @param locale A BCP47 language tag.
 */
export const fetchFileViewerIntlForLocale = async (locale) => {
    const dropboxLocale = bcp47ToDropboxLocale(locale);
    const strings = await fetchFileViewerStringsForLocale(dropboxLocale);
    return createIntl({ locale: dropboxLocale, messages: strings, defaultLocale: 'en' });
};
//# sourceMappingURL=get_lang.preenvesnextjs.map
